'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ExclamationMarkTriangleXs } from '@lichtblick/photon/icons/symbols/exclamation-mark-triangle-xs';
import { Colors } from '@lichtblick/theme';
import { useTracker } from '@lichtblick/tracker';
import { FieldFooter } from '@lichtblick/ui-components';
import { BoxCTA } from '@lichtblick/ui-components/atoms/BoxCTA/BoxCTA';
import { CheckboxInput } from '@lichtblick/ui-components/molecules/Checkbox/CheckboxInput';
import { Textfield } from '@lichtblick/ui-components/molecules/Textfield/Textfield';
import { CheckGroup } from '@lichtblick/ui-components/organisms/CheckGroup/CheckGroup';
import { useParams } from 'next/navigation';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AImage } from '../../AImage/AImage';
import { MTextIntro } from '../../MTextIntro/MTextIntro';
import { GridColumn, GridRow } from '../../shared/GridContainer/GridContainer';
import { Section } from '../shared/Section';
import { Form, StyledGridContainer } from './LeadForm.styles';
import { MarketingLegalText, marketingLegalText } from './LegalText';
export const REQUEST_ID = uuidv4();
const DEFAULT_REQUIRED_ERROR_MESSAGE = 'Hier fehlt noch eine Angabe';
const DEFAULT_INVALID_ERROR_MESSAGE = 'Bitte überprüfe deine Angabe';
export const SERVER_URL = process.env.NEXT_PUBLIC_GRAPHQL_SERVER || '';
export const LeadForm = ({ dynamicsID, image, keepImageAspectRatio, target, textIntro })=>{
    const { web_campaign_id } = useParams();
    const webCampaignId = Array.isArray(web_campaign_id) ? web_campaign_id[0] : web_campaign_id;
    const [hasSubmitError, setHasSubmitError] = useState(false);
    const [isLoadingMarketingLead, setIsLoadingMarketingLead] = useState(false);
    const [missingOptin, setMissingOptin] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState(undefined);
    const { trackCustomEvent } = useTracker();
    const handleError = ()=>{
        setHasSubmitError(true);
        setIsLoadingMarketingLead(false);
    };
    const onSubmit = useCallback((event)=>{
        event.preventDefault();
        const email = new FormData(event.currentTarget).get('email')?.toString();
        if (!target.cached_url) {
            return;
        }
        if (!email) {
            setEmailErrorMessage(DEFAULT_REQUIRED_ERROR_MESSAGE);
            return;
        }
        if (!/^([\w.%+-]+)@([\w-]+\.)+(\w{2,})$/.test(email)) {
            setEmailErrorMessage(DEFAULT_INVALID_ERROR_MESSAGE);
            return;
        }
        const lead = {
            email,
            contentId: dynamicsID,
            legalText: marketingLegalText,
            requestId: REQUEST_ID,
            target: target.cached_url,
            webCampaignId
        };
        setIsLoadingMarketingLead(true);
        setHasSubmitError(false);
        import('../../../api/lichtblick/graphqlClient').then(({ sdk })=>sdk.eaasCreateMarketingLead({
                input: {
                    lead
                }
            }).then((response)=>{
                if (!response.eaasCreateMarketingLead.isSent) {
                    handleError();
                    return;
                }
                trackCustomEvent({
                    eventName: 'form_submit_solar_ratgeber',
                    customProps: {
                        unique_id: REQUEST_ID,
                        partner: true
                    }
                });
                location.assign('/solar-ratgeber-danke/');
            }).catch(handleError));
    }, // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
    [
        dynamicsID,
        target.cached_url,
        webCampaignId,
        trackCustomEvent,
        handleError
    ]);
    return /*#__PURE__*/ _jsx(Section, {
        children: /*#__PURE__*/ _jsx(StyledGridContainer, {
            $backgroundColor: Colors.FadedMoss,
            children: /*#__PURE__*/ _jsxs(GridRow, {
                children: [
                    /*#__PURE__*/ _jsx(GridColumn, {
                        $isAlignedMiddle: false,
                        $width: 6,
                        children: image?.[0] && /*#__PURE__*/ _jsx(AImage, {
                            ...image[0],
                            isBackground: !keepImageAspectRatio
                        })
                    }),
                    /*#__PURE__*/ _jsxs(GridColumn, {
                        $isAlignedMiddle: true,
                        $width: 6,
                        children: [
                            textIntro?.[0] && /*#__PURE__*/ _jsx(MTextIntro, {
                                ...textIntro[0]
                            }),
                            /*#__PURE__*/ _jsxs(Form, {
                                onSubmit: onSubmit,
                                children: [
                                    /*#__PURE__*/ _jsx(Textfield, {
                                        label: "Bitte gib deine E-Mail Adresse ein",
                                        name: "email",
                                        onChange: (event)=>setEmailErrorMessage(event.target.value ? undefined : DEFAULT_REQUIRED_ERROR_MESSAGE),
                                        onInvalid: (e)=>{
                                            e.preventDefault();
                                            setEmailErrorMessage(e.currentTarget.value ? DEFAULT_INVALID_ERROR_MESSAGE : DEFAULT_REQUIRED_ERROR_MESSAGE);
                                        },
                                        required: true,
                                        statusLabel: emailErrorMessage,
                                        statusType: emailErrorMessage ? 'error' : 'default',
                                        type: "email"
                                    }),
                                    /*#__PURE__*/ _jsx(CheckGroup, {
                                        children: /*#__PURE__*/ _jsx(CheckboxInput, {
                                            hasError: missingOptin,
                                            labelSlot: /*#__PURE__*/ _jsx(MarketingLegalText, {}),
                                            name: "optInMarketing",
                                            onChange: (e)=>setMissingOptin(!e.currentTarget.validity.valid),
                                            onInvalid: (e)=>{
                                                e.preventDefault();
                                                setMissingOptin(!e.currentTarget.validity.valid);
                                            },
                                            required: true,
                                            statusLabel: missingOptin ? DEFAULT_REQUIRED_ERROR_MESSAGE : undefined
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(BoxCTA, {
                                        as: "button",
                                        isDisabled: isLoadingMarketingLead,
                                        isLoading: isLoadingMarketingLead,
                                        type: "submit",
                                        children: "Senden"
                                    }),
                                    Boolean(hasSubmitError) && /*#__PURE__*/ _jsx(FieldFooter, {
                                        fieldFooterLabel: "Beim Senden der Anfrage ist ein unerwarteter Fehler aufgetreten. Bitte versuche es später noch einmal.",
                                        fieldFooterType: "error",
                                        icon: ExclamationMarkTriangleXs
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        })
    });
};

//# sourceMappingURL=LeadForm.js.map